import type { GetSettingsListOneResponseI } from "@Common/types/bo/settings/settings";
import type { ComponentPropI } from "@Common/types/resources";
import { CheckoutForm } from "@Src/components/Checkout";
import { Box, Card, CardContent, Typography } from "@mui/material";
import { List, useListContext } from "react-admin";

const SettingsContent = () => {
	const { data, isLoading } = useListContext<GetSettingsListOneResponseI>();

	if (isLoading) return null;

	const settings = data?.[0];

	if (!settings) return null;

	// Calculate the first day of next month for reset date
	const getNextMonthResetDate = () => {
		const now = new Date();
		const nextMonth = new Date(
			Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1),
		);
		return nextMonth.toLocaleDateString("en-US", {
			month: "long",
			day: "numeric",
			year: "numeric",
			timeZone: "UTC",
		});
	};

	return (
		<>
			<Box sx={{ maxWidth: 600, margin: "20px auto" }}>
				<Card>
					<CardContent>
						<Typography variant="h5" component="h2" gutterBottom>
							Current Subscription Plan
						</Typography>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body1" gutterBottom>
								<strong>Plan:</strong>{" "}
								{settings.name.charAt(0).toUpperCase() + settings.name.slice(1)}
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Price:</strong> $
								{(settings.price_cents / 100).toFixed(2)} / month
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Max Forms:</strong>{" "}
								{settings.max_forms === null ? "Unlimited" : settings.max_forms}
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Max Responses per Month:</strong>{" "}
								{settings.max_applications_per_month.toLocaleString()}
							</Typography>
						</Box>
					</CardContent>
				</Card>

				<Card sx={{ mt: 2 }}>
					<CardContent>
						<Typography variant="h5" component="h2" gutterBottom>
							Quota Usage
						</Typography>
						<Box sx={{ mt: 2 }}>
							<Typography variant="body1" gutterBottom>
								<strong>Forms:</strong>{" "}
								{settings.remaining_forms === null
									? `${settings.current_forms_count} created (Unlimited)`
									: `${settings.current_forms_count} / ${settings.max_forms} created (${settings.remaining_forms} additional forms available)`}
							</Typography>
							<Typography variant="body1" gutterBottom>
								<strong>Responses this month:</strong>{" "}
								{`${settings.current_applications_count} / ${settings.max_applications_per_month} used (${settings.remaining_applications} remaining)`}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Response quota resets on {getNextMonthResetDate()} (UTC)
							</Typography>
						</Box>
					</CardContent>
				</Card>
			</Box>
			<CheckoutForm currentPlanId={settings.id} />
		</>
	);
};

export const SettingsList = (props: ComponentPropI) => {
	return (
		<List
			disableAuthentication={props.isPublic}
			pagination={false}
			exporter={false}
		>
			<SettingsContent />
		</List>
	);
};
